import { useEffect, useMemo, useState } from 'react';
import { usePageInfo, useStateRef } from '@op/hooks/common';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { useUpdateEffect } from 'ahooks';
import { SortBy } from 'tea-component/lib/table/addons';
import { getUgcTrendsList } from '@opm/services';
import { useStableValue } from '@opm/utils/stableKey';
import { DEFAULT_PAGE_INFO } from '../constants';
import {
  ITrendItem,
  TStatusTab,
  IUgcVideoItem,
  toVideoCardItem,
} from '../types';

/**
 * 把 trend 行内嵌的 `top_related_videos` 每项预先适配成 VideoCard prop 形状，
 * 这样下游 UgcTrendVideoThumb / PreviewThumbnails 直接消费，组件里不再做字段映射。
 */
function normalizeTrendRow(row: any): any {
  if (!row?.top_related_videos?.length) return row;
  return {
    ...row,
    top_related_videos: row.top_related_videos.map((v: IUgcVideoItem) =>
      toVideoCardItem(v),
    ),
  };
}

const VIEW_MODE_STORAGE_KEY = 'ugc_trends_view_mode';

function getStoredViewMode(fallback: 'card' | 'table'): 'card' | 'table' {
  try {
    const stored = sessionStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (stored === 'card' || stored === 'table') return stored;
  } catch {
    /* ignore */
  }
  return fallback;
}

const EMPTY_MAX: any = {
  tweets_view: 0,
  tweets_like: 0,
  videos: 0,
};

/** 后端 sort_item 白名单（list.md 严格校验） */
const SORT_WHITELIST = new Set(['tweets_view', 'tweets_like', 'videos']);

export function useUgcTrendsData({
  filter,
  defaultPageInfo = DEFAULT_PAGE_INFO,
  defaultViewMode = 'card',
  closeDrawer,
}: {
  filter: any;
  defaultPageInfo?: { page: number; page_size: number };
  defaultViewMode?: 'card' | 'table';
  closeDrawer: () => void;
}) {
  const [statusTabRef, statusTab, setStatusTab] =
    useStateRef<TStatusTab>('hot');
  const [viewMode, setViewMode] = useState<'card' | 'table'>(() =>
    getStoredViewMode(defaultViewMode),
  );
  const [keywordRef, keyword, setKeyword] = useStateRef<string>('');
  const [sortByRef, sortBy, setSortBy] = useStateRef<SortBy<string>>({
    order: 'desc',
    by: 'tweets_view',
  });
  const {
    pageInfo,
    changePageInfo,
    resetPageInfo,
    nextPage,
    changePageInfoNoRender,
  } = usePageInfo({ defaultPageInfo });

  const [cardDataRef, cardRecords, setCardData] = useStateRef<ITrendItem[]>([]);
  const [tableRecords, setTableRecords] = useState<ITrendItem[]>([]);
  const [total, setTotal] = useState(0);
  const [listMax, setListMax] = useState<any>(EMPTY_MAX);

  const { wrapperCancelRequest, loading, loadingRef } = useCancelRequestHook({
    clear: () => {
      setTableRecords([]);
      setTotal(0);
      setListMax(EMPTY_MAX);
    },
  });

  function resetAllData() {
    setCardData([]);
    setTableRecords([]);
    setTotal(0);
    setListMax(EMPTY_MAX);
    resetPageInfo();
    closeDrawer();
  }

  function changeStatusTab(tab: TStatusTab) {
    if (tab === statusTabRef.current) return;
    setStatusTab(tab);
    resetAllData();
  }

  function changeSortBy(newSortBy: SortBy<string>) {
    setSortBy(newSortBy);
    resetAllData();
  }

  function changeKeyword(val: string) {
    if (val === keywordRef.current) return;
    resetAllData();
    setKeyword(val);
  }

  function changeViewMode(mode: 'card' | 'table') {
    if (mode === viewMode) return;
    setViewMode(mode);
    try {
      sessionStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
    } catch (_e) {
      /* ignore */
    }
    resetAllData();
  }

  function loadNextPage() {
    if (loadingRef.current) return;
    if (viewMode !== 'card') return;
    if (total === 0) return;

    const loadedCount = cardDataRef.current?.length || 0;
    if (loadedCount >= total && total > 0) return;
    nextPage();
  }

  function changeTablePageInfo(newPageInfo?: {
    page: number;
    page_size: number;
  }) {
    setTableRecords([]);
    changePageInfo(newPageInfo);
  }

  const stableFilter = useStableValue(filter);
  const stablePageInfo = useStableValue(pageInfo);

  useUpdateEffect(() => {
    if (!stableFilter) return;
    resetAllData();
  }, [stableFilter]);

  const params = useMemo(() => {
    if (!stableFilter) return null;
    const rawSort = sortByRef.current?.by || 'tweets_view';
    const sortItem = SORT_WHITELIST.has(rawSort) ? rawSort : 'tweets_view';
    return {
      ...stableFilter,
      ...stablePageInfo,
      tab: statusTabRef.current,
      sort_item: sortItem,
      sort_model: sortByRef.current?.order,
      search_text: keywordRef.current,
    };
  }, [stableFilter, stablePageInfo, statusTab, sortBy, keyword]);

  function request() {
    if (!params) return;
    wrapperCancelRequest(getUgcTrendsList, params)
      .then((response: any) => {
        const { list = [], total: totalCount = 0, max = {} } = response || {};
        const normalized = list.map(normalizeTrendRow);

        if (viewMode === 'card') {
          if (pageInfo.page === 1) {
            setCardData(normalized);
          } else {
            setCardData([...(cardDataRef.current || []), ...normalized]);
          }
        } else {
          setTableRecords(normalized);
        }

        setTotal(totalCount);
        setListMax(max);
      })
      .catch(() => {
        if (viewMode === 'card' && pageInfo.page > 1) {
          const prevPage = Math.max(
            1,
            Math.ceil((cardDataRef.current?.length || 0) / pageInfo.page_size),
          );
          changePageInfoNoRender({ ...pageInfo, page: prevPage });
        }
      });
  }

  useEffect(() => {
    if (!params) return;
    request();
  }, [params, viewMode]);

  const currentRecords = viewMode === 'card' ? cardRecords || [] : tableRecords;
  const hasMore = (cardRecords?.length || 0) < total;
  const isEmpty = !loading && currentRecords.length === 0;
  const isFirstLoading =
    loading && pageInfo.page === 1 && currentRecords.length === 0;
  const isPaginationLoading =
    loading && viewMode === 'card' && (cardRecords?.length || 0) > 0;

  return {
    cardRecords: cardRecords || [],
    tableRecords,
    total,
    listMax,
    hasMore,
    pageInfo,
    changeTablePageInfo,
    loadNextPage,
    loading,
    isFirstLoading,
    isEmpty,
    isPaginationLoading,
    statusTab: statusTab as TStatusTab,
    setStatusTab: changeStatusTab,
    sortBy: sortBy as SortBy<string>,
    setSortBy: changeSortBy,
    keyword: keyword || '',
    setKeyword: changeKeyword,
    viewMode,
    setViewMode: changeViewMode,
  };
}
